<template>
  <div class="status-manager">
    <div class="header">
      <h2>Status Changes Manager</h2>
      <button @click="showCreateModal = true" class="btn btn-primary">
        Create New Status Change
      </button>
    </div>

    <div class="explanation-box mb-4">
      <h4>About Status Changes</h4>
      <p>Status changes are automated updates to document or analysis states that can be triggered by workflow checks. They help maintain document lifecycle and track progress through your review process.</p>
      
      <h5>Key Concepts:</h5>
      <ul>
        <li><strong>Name:</strong> A descriptive name for the status change (e.g., "Mark as Reviewed", "Flag for Revision")</li>
        <li><strong>Target Field:</strong> The field to be updated (e.g., "document_status", "review_status")</li>
        <li><strong>New Status:</strong> The value to set when this change is triggered</li>
        <li><strong>Propagate:</strong> Whether this status change should affect related documents</li>
      </ul>

      <h5>Usage:</h5>
      <p>Status changes can be assigned to automated checks as either success or failure actions. For example:</p>
      <ul>
        <li>On successful validation → "Mark as Approved"</li>
        <li>On failed compliance check → "Flag for Review"</li>
      </ul>
    </div>

    <!-- Status Changes List -->
    <div class="status-list">
      <table class="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>New Status</th>
            <th>Target Field</th>
            <th>Propagate</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="status in statusChanges" :key="status.id">
            <td>{{ status.name }}</td>
            <td>{{ status.new_status }}</td>
            <td>{{ status.target_field }}</td>
            <td>{{ status.propagate ? 'Yes' : 'No' }}</td>
            <td>{{ formatDate(status.created_at) }}</td>
            <td>
              <button @click="editStatus(status)" class="btn btn-sm btn-outline-primary me-2">
                Edit
              </button>
              <button @click="confirmDelete(status)" class="btn btn-sm btn-outline-danger">
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create/Edit Modal -->
    <Modal v-if="showCreateModal || editingStatus" :show="true" @close="closeModal">
      <template #header>
        <h3>{{ editingStatus ? 'Edit Status Change' : 'Create New Status Change' }}</h3>
      </template>
      
      <template #body>
        <form @submit.prevent="saveStatus" class="status-form">
          <div class="mb-3">
            <label for="name" class="form-label">Name</label>
            <input
              v-model="statusForm.name"
              type="text"
              class="form-control"
              id="name"
              required
              placeholder="e.g., Mark as Reviewed"
            >
          </div>

          <div class="mb-3">
            <label for="target_field" class="form-label">Target Field</label>
            <select
              v-model="statusForm.target_field"
              class="form-select"
              id="target_field"
              required
            >
              <option value="">Select a field to update</option>
              <option value="document_status">Document Status</option>
              <option value="review_status">Review Status</option>
              <option value="analysis_status">Analysis Status</option>
              <option value="workflow_status">Workflow Status</option>
            </select>
          </div>

          <div class="mb-3">
            <label for="new_status" class="form-label">New Status</label>
            <select
              v-model="statusForm.new_status"
              class="form-select"
              id="new_status"
              required
            >
              <option value="">Select a status value</option>
              <template v-if="statusForm.target_field === 'document_status'">
                <option value="PENDING">Pending</option>
                <option value="IN_REVIEW">In Review</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="NEEDS_REVISION">Needs Revision</option>
              </template>
              <template v-else-if="statusForm.target_field === 'review_status'">
                <option value="NOT_STARTED">Not Started</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="ON_HOLD">On Hold</option>
              </template>
              <template v-else-if="statusForm.target_field === 'analysis_status'">
                <option value="QUEUED">Queued</option>
                <option value="PROCESSING">Processing</option>
                <option value="COMPLETED">Completed</option>
                <option value="FAILED">Failed</option>
              </template>
              <template v-else-if="statusForm.target_field === 'workflow_status'">
                <option value="PENDING">Pending</option>
                <option value="ACTIVE">Active</option>
                <option value="COMPLETED">Completed</option>
                <option value="FAILED">Failed</option>
                <option value="CANCELLED">Cancelled</option>
              </template>
            </select>
          </div>

          <div class="mb-3">
            <label for="description" class="form-label">Description</label>
            <textarea
              v-model="statusForm.description"
              class="form-control"
              id="description"
              rows="3"
              placeholder="Explain when and why this status change should occur"
            ></textarea>
          </div>

          <div class="mb-3">
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="propagate"
                v-model="statusForm.propagate"
              >
              <label class="form-check-label" for="propagate">
                Propagate Status Change
              </label>
              <small class="form-text text-muted d-block mt-1">
                When enabled, this status change will affect related documents in the workflow
              </small>
            </div>
          </div>
        </form>
      </template>

      <template #footer>
        <button @click="closeModal" class="btn btn-secondary me-2">Cancel</button>
        <button @click="saveStatus" class="btn btn-primary">Save</button>
      </template>
    </Modal>

    <!-- Delete Confirmation Modal -->
    <Modal v-if="showDeleteModal" :show="true" @close="showDeleteModal = false">
      <template #header>
        <h3>Confirm Delete</h3>
      </template>
      
      <template #body>
        <p>Are you sure you want to delete the status change "{{ statusToDelete?.name }}"?</p>
      </template>

      <template #footer>
        <button @click="showDeleteModal = false" class="btn btn-secondary me-2">Cancel</button>
        <button @click="deleteStatus" class="btn btn-danger">Delete</button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import Modal from './Modal.vue'
import api from '../services/api'

export default {
  name: 'StatusChangeManager',
  components: { Modal },

  setup() {
    const statusChanges = ref([])
    const showCreateModal = ref(false)
    const showDeleteModal = ref(false)
    const editingStatus = ref(null)
    const statusToDelete = ref(null)

    const statusForm = ref({
      name: '',
      new_status: '',
      target_field: '',
      description: '',
      propagate: false
    })

    const loadStatusChanges = async () => {
      try {
        const response = await api.get('/status-changes/')
        statusChanges.value = response.data
      } catch (error) {
        console.error('Error loading status changes:', error)
      }
    }

    const saveStatus = async () => {
      try {
        const payload = { ...statusForm.value }

        if (editingStatus.value) {
          await api.put(`/status-changes/${editingStatus.value.id}/`, payload)
        } else {
          await api.post('/status-changes/', payload)
        }

        await loadStatusChanges()
        closeModal()
      } catch (error) {
        console.error('Error saving status change:', error)
      }
    }

    const editStatus = (status) => {
      editingStatus.value = status
      statusForm.value = {
        name: status.name,
        new_status: status.new_status,
        target_field: status.target_field,
        description: status.description || '',
        propagate: status.propagate
      }
    }

    const confirmDelete = (status) => {
      statusToDelete.value = status
      showDeleteModal.value = true
    }

    const deleteStatus = async () => {
      if (!statusToDelete.value) return

      try {
        await api.delete(`/status-changes/${statusToDelete.value.id}/`)
        await loadStatusChanges()
        showDeleteModal.value = false
        statusToDelete.value = null
      } catch (error) {
        console.error('Error deleting status change:', error)
      }
    }

    const closeModal = () => {
      showCreateModal.value = false
      editingStatus.value = null
      statusToDelete.value = null
      statusForm.value = {
        name: '',
        new_status: '',
        target_field: '',
        description: '',
        propagate: false
      }
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleString()
    }

    const getStatusOptions = (field) => {
      const options = {
        document_status: [
          { value: 'PENDING', label: 'Pending' },
          { value: 'IN_REVIEW', label: 'In Review' },
          { value: 'APPROVED', label: 'Approved' },
          { value: 'REJECTED', label: 'Rejected' },
          { value: 'NEEDS_REVISION', label: 'Needs Revision' }
        ],
        review_status: [
          { value: 'NOT_STARTED', label: 'Not Started' },
          { value: 'IN_PROGRESS', label: 'In Progress' },
          { value: 'COMPLETED', label: 'Completed' },
          { value: 'ON_HOLD', label: 'On Hold' }
        ],
        // ... other status mappings
      };
      return options[field] || [];
    };

    onMounted(() => {
      loadStatusChanges()
    })

    return {
      statusChanges,
      showCreateModal,
      showDeleteModal,
      editingStatus,
      statusToDelete,
      statusForm,
      saveStatus,
      editStatus,
      confirmDelete,
      deleteStatus,
      closeModal,
      formatDate,
      getStatusOptions
    }
  }
}
</script>

<style scoped>
.status-manager {
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  background-color: #f3f4f6;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.header h2 {
  margin: 0;
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
}

.explanation-box {
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.status-list {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
}

.table th {
  background-color: #f9fafb;
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
}

.table tr:last-child td {
  border-bottom: none;
}

.status-form {
  max-width: 600px;
  margin: 0 auto;
}

.form-label {
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-control,
.form-select {
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  width: 100%;
  transition: border-color 0.15s ease-in-out;
}

.form-control:focus,
.form-select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-check {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-check-input {
  width: 1rem;
  height: 1rem;
  margin-top: 0.25rem;
}

.form-check-label {
  margin-bottom: 0;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
}

.btn-primary {
  background-color: #3b82f6;
  border: 1px solid transparent;
  color: white;
}

.btn-primary:hover {
  background-color: #2563eb;
}

.btn-secondary {
  background-color: white;
  border: 1px solid #d1d5db;
  color: #374151;
}

.btn-secondary:hover {
  background-color: #f9fafb;
}

.btn-danger {
  background-color: #ef4444;
  border: 1px solid transparent;
  color: white;
}

.btn-danger:hover {
  background-color: #dc2626;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.btn-outline-primary {
  color: #3b82f6;
  border: 1px solid #3b82f6;
  background-color: transparent;
}

.btn-outline-primary:hover {
  background-color: #3b82f6;
  color: white;
}

.btn-outline-danger {
  color: #ef4444;
  border: 1px solid #ef4444;
  background-color: transparent;
}

.btn-outline-danger:hover {
  background-color: #ef4444;
  color: white;
}

.me-2 {
  margin-right: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.form-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}
</style> 